export interface HierarchyNodeInput {
  id: string
  label: string
  parentId: string | null
}

export interface TreeNode {
  id: string
  label: string
  children: TreeNode[]
}

export function buildHierarchyTree(nodes: HierarchyNodeInput[]): {
  roots: TreeNode[]
  byId: Map<string, TreeNode>
} {
  const byId = new Map<string, TreeNode>()
  for (const n of nodes) byId.set(n.id, { id: n.id, label: n.label, children: [] })

  const roots: TreeNode[] = []
  for (const n of nodes) {
    const node = byId.get(n.id)!
    const parent = n.parentId ? byId.get(n.parentId) : undefined
    if (parent && parent.id !== node.id) parent.children.push(node)
    else roots.push(node) // pas de parent connu ⇒ racine
  }
  return { roots, byId }
}

export function collectSubtreeIds(byId: Map<string, TreeNode>, id: string): string[] {
  const out = new Set<string>()
  const walk = (nodeId: string) => {
    if (out.has(nodeId)) return
    out.add(nodeId)
    const node = byId.get(nodeId)
    if (node) for (const child of node.children) walk(child.id)
  }
  walk(id)
  return Array.from(out)
}

/**
 * Restreint l'arbre aux nœuds qui respectent les CONDITIONS du widget (`allowedIds`, calculé par le
 * moteur de dashboard — cf. `hierarchyConditions.js`).
 *
 * Un nœud gardé dont le parent est écarté est RACCROCHÉ à son premier ancêtre gardé, sinon à la
 * racine : écarter un PCA archivé ne doit pas faire disparaître ses filiales actives. Même règle
 * que le back pour les nœuds masqués par les droits (`getReferentialHierarchy`), garde-fou de
 * cycle compris — rien n'interdit A → B → A en base.
 *
 * `allowedIds` absent (`null`/`undefined`) = pas de condition, ou résultat pas encore connu :
 * l'arbre est rendu tel quel.
 */
export function pruneHierarchyNodes(
  nodes: HierarchyNodeInput[],
  allowedIds: Iterable<string> | null | undefined
): HierarchyNodeInput[] {
  if (allowedIds == null) return nodes
  const allowed = new Set(allowedIds)
  const parentOf = new Map(nodes.map(n => [n.id, n.parentId]))
  const kept: HierarchyNodeInput[] = []
  for (const n of nodes) {
    if (!allowed.has(n.id)) continue
    const seen = new Set<string>([n.id])
    let current = n.parentId
    let parentId: string | null = null
    while (current && !seen.has(current)) {
      if (allowed.has(current) && parentOf.has(current)) {
        parentId = current
        break
      }
      seen.add(current)
      current = parentOf.get(current) ?? null
    }
    kept.push({ ...n, parentId })
  }
  return kept
}

export interface ValueFilter {
  mode?: string
  values?: unknown[]
}

/**
 * Entités autorisées par le réglage « Valeurs proposées » (`widgetProps.valueFilter`), comparées sur
 * leur LIBELLÉ — même réglage, même comparaison que les listes déroulantes (`restreindreValeurs`) :
 * en chaîne, espaces de bord retirés, sans pliage de casse.
 *
 * - `exclude` : toutes les entités sauf celles listées ;
 * - `include` : seulement celles listées.
 *
 * Le résultat passe ensuite par `pruneHierarchyNodes` : une entité écartée disparaît de l'arbre,
 * mais ses descendants gardés se RACCROCHENT à leur premier ancêtre gardé. Exclure un organisme ne
 * fait donc pas disparaître ses directions.
 *
 * ⚠️ Comme pour les listes, seule la liste PROPOSÉE est restreinte : tant que rien n'est coché, la
 * page n'est pas filtrée.
 *
 * @returns les ids autorisés, ou `null` sans réglage (aucune contrainte)
 */
export function idsSelonValeurs(
  nodes: HierarchyNodeInput[],
  valueFilter: ValueFilter | null | undefined
): Set<string> | null {
  const valeurs = Array.isArray(valueFilter?.values) ? valueFilter!.values : []
  if (valeurs.length === 0) return null
  const cles = new Set(valeurs.map(v => String(v).trim()))
  const listee = (n: HierarchyNodeInput) => cles.has(String(n.label).trim())
  if (valueFilter?.mode === "exclude") return new Set(nodes.filter(n => !listee(n)).map(n => n.id))
  if (valueFilter?.mode === "include") return new Set(nodes.filter(listee).map(n => n.id))
  return null
}

/**
 * Intersection de deux contraintes d'ids, `null` valant « aucune contrainte ». Sert à combiner les
 * CONDITIONS du widget (`allowedIds`) et le réglage « Valeurs proposées ».
 */
export function combinerAutorises(
  a: Iterable<string> | null | undefined,
  b: Iterable<string> | null | undefined
): Set<string> | null {
  if (a == null && b == null) return null
  if (a == null) return new Set(b!)
  if (b == null) return new Set(a)
  const ensembleB = new Set(b)
  return new Set(Array.from(a).filter(id => ensembleB.has(id)))
}

/**
 * Complète des ids autorisés par TOUS leurs ancêtres (`keepAncestors`).
 *
 * Sert quand l'arbre est restreint aux entités PRÉSENTES dans le dashboard (conditions du widget) :
 * une direction sans évaluation propre — « DAF », dont seuls les services sont évalués — serait
 * sinon écartée, et ses services raccrochés à la racine. L'arbre perdrait sa forme, et l'on ne
 * pourrait plus cocher la direction pour voir ses services. Recette CRC du 13/10/2026.
 *
 * `null` reste `null` (aucune contrainte). Garde-fou de cycle : rien n'interdit A → B → A en base.
 */
export function avecAncetres(
  nodes: HierarchyNodeInput[],
  allowedIds: Iterable<string> | null | undefined
): Set<string> | null {
  if (allowedIds == null) return null
  const parentOf = new Map(nodes.map(n => [n.id, n.parentId]))
  const sortie = new Set<string>()
  for (const id of allowedIds) {
    let courant: string | null | undefined = id
    while (courant && !sortie.has(courant)) {
      sortie.add(courant)
      courant = parentOf.get(courant)
    }
  }
  return sortie
}

/**
 * Sélection suivante en mode CYCLE (`selectionCycle`), au clic sur un nœud :
 *
 *   1ᵉʳ clic — le nœud SEUL est coché ;
 *   2ᵉ clic  — le nœud ET son sous-arbre ;
 *   3ᵉ clic  — le nœud et son sous-arbre décochés.
 *
 * Demande CRC du 13/10/2026 : voir les chiffres d'une entité parente seule obligeait à décocher un
 * à un tous ses enfants (« fastidieux, ex. DG »). Une feuille passe directement de coché à décoché.
 *
 * ⚠️ L'état se lit sur la sélection, pas sur un compteur de clics : il reste juste si la sélection
 * a changé par ailleurs (effacement, autre nœud).
 */
export function selectionSuivanteCycle(
  byId: Map<string, TreeNode>,
  checked: Iterable<string>,
  nodeId: string
): Set<string> {
  const next = new Set(checked)
  const sousArbre = collectSubtreeIds(byId, nodeId)
  const toutCoche = sousArbre.every(id => next.has(id))
  if (!next.has(nodeId)) {
    next.add(nodeId)
  } else if (!toutCoche) {
    sousArbre.forEach(id => next.add(id))
  } else {
    sousArbre.forEach(id => next.delete(id))
  }
  return next
}
